// src/components/assistant/AssistantFormModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

export default function AssistantFormModal({
  show,
  onHide,
  onSave,
  assistant = null, // null for create, object for edit
  loading,
  t
}) {
  const isEditMode = !!assistant;

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    system_prompt: '',
    is_active: true
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (assistant) {
      setFormData({
        name: assistant.name || '',
        description: assistant.description || '',
        system_prompt: assistant.system_prompt || '',
        is_active: assistant.is_active !== undefined ? assistant.is_active : true
      });
    } else {
      setFormData({
        name: '',
        description: '',
        system_prompt: '',
        is_active: true
      });
    }
    setHasChanges(false);
  }, [assistant, show]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleClose = () => {
    if (hasChanges && !window.confirm(t?.unsavedChanges || 'You have unsaved changes. Are you sure you want to close?')) {
      return;
    }
    onHide();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.system_prompt.trim()) {
      alert(t?.fillRequired || 'Please fill in all required fields');
      return;
    }
    onSave(formData);
  };

  const remainingName = 255 - formData.name.length;
  const remainingDesc = 500 - formData.description.length;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton style={{ borderBottom: '2px solid #f0f0f0' }}>
        <Modal.Title>
          {isEditMode ? '✏️' : '✨'} {isEditMode ? (t?.editAssistant || 'Edit Assistant') : (t?.createNew || 'Create New Assistant')}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className="mb-3">
            <Form.Label>
              {t?.assistantName || 'Assistant Name'} <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t?.namePlaceholder || 'e.g., Technical Support Agent'}
              maxLength={255}
              required
              autoFocus
            />
            <Form.Text className="text-muted">
              {remainingName} {t?.charactersRemaining || 'characters remaining'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t?.assistantDescription || 'Description'}</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t?.descriptionPlaceholder || 'Brief description of this assistant\'s purpose (optional)'}
              maxLength={500}
            />
            <Form.Text className="text-muted">
              {remainingDesc} {t?.charactersRemaining || 'characters remaining'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              {t?.systemPrompt || 'System Prompt'} <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={20}
              value={formData.system_prompt}
              onChange={(e) => handleChange('system_prompt', e.target.value)}
              placeholder={t?.promptPlaceholder || 'Enter the complete system prompt...'}
              style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}
              required
            />
          </Form.Group>

          {isEditMode && (
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is-active-switch"
                label={formData.is_active ? (t?.toggleActive || 'Active') : (t?.toggleInactive || 'Inactive')}
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
            </Form.Group>
          )}

          <div style={{
            backgroundColor: '#e7f3ff',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '0.9rem',
            color: '#0d6efd'
          }}>
            💡 {t?.tipMessage || 'Tip: Define the assistant\'s role, tone, rules, and response format in the system prompt.'}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '2px solid #f0f0f0' }}>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            {t?.cancel || 'Cancel'}
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading ? '⏳' : '💾'} {isEditMode ? (t?.saveChanges || 'Save Changes') : (t?.saveAssistant || 'Save Assistant')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
